import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMinutes } from "@/lib/interventions";
import { fmtEur } from "@/lib/reports";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { ReportRow } from "@/components/reports/ReportRow";
import { ReportActivities } from "@/components/reports/ReportActivities";

const sum = (items, fn) => items.reduce((a, r) => a + (fn(r) || 0), 0);

export const InterventionReports = ({ itv, onChanged, reloadKey }) => {
  const { can, me } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/service-reports", { params: { intervention_id: itv.id, limit: 100 } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), [itv.id]);
  useEffect(() => { load(); }, [load, reloadKey]);
  const canNew = can("reports:create") && !itv.archived_at && ["in_corso", "completato"].includes(itv.status) && (can("reports:confirm") || itv.assigned_ids?.includes(me.user.id));
  const create = async () => {
    setBusy(true);
    try { const r = await api.post(`/interventions/${itv.id}/reports`, {}); toast.success(`Rapportino ${r.data.number} creato`); nav(`/rapportini/${r.data.id}`); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const live = (items || []).filter((r) => r.status !== "annullato");
  const econ = live.some((r) => r.charge);
  return (
    <Panel className="p-5" data-testid="itv-reports">
      <div className="mb-2 flex items-center justify-between gap-2"><SectionLabel>Rapportini</SectionLabel>
        {canNew && <Button size="sm" className="gap-1" onClick={create} disabled={busy} data-testid="itv-new-report"><FilePlus2 className="h-4 w-4" />Compila rapportino</Button>}</div>
      {items && !items.length && <p className="text-sm text-slate-500">Nessun rapportino.{itv.report_status === "da_compilare" ? " Intervento completato: rapportino da compilare." : ""}</p>}
      <ul className="divide-y divide-line">{(items || []).map((r) => <ReportRow key={r.id} rep={r} showCompany={false} />)}</ul>
      {live.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 rounded-control bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="itv-reports-totals">
          <span>Totale effettivo <b className="text-ink">{fmtMinutes(sum(live, (r) => r.effective_minutes))}</b></span>
          <span>Totale fatturabile <b className="text-ink">{fmtMinutes(sum(live, (r) => r.billable_minutes))}</b></span>
          {econ && <span>Imponibile <b className="text-ink">{fmtEur(sum(live, (r) => r.charge?.taxable))}</b></span>}
          {econ && <span>Coperto da pacchetto <b className="text-emerald-700">{fmtEur(sum(live, (r) => r.charge?.package_covered_net))}</b></span>}
          {econ && <span>Da fatturare <b className="text-ink">{fmtEur(sum(live, (r) => r.charge?.to_invoice_net))}</b></span>}
        </div>)}
      <ReportActivities itv={itv} canGenerate={canNew} reloadKey={reloadKey} onDone={() => { load(); onChanged?.(); }} />
    </Panel>
  );
};
